import path from "node:path";

import { globby } from "globby";
import { type AstNode, AstUtils, URI } from "langium";
import { NodeFileSystem } from "langium/node";
import { DiagnosticSeverity } from "vscode-languageserver";
import { BUILTIN_LIBRARY, BUILTIN_LIBRARY_URI } from "../language/builtin/index.js";
import type {
	Alias,
	Declaration,
	File,
	Import,
	Module,
	ReferenceDirective,
	ReferenceType,
} from "../language/generated/ast.js";
import { IprotoModuleBuilder, type IprotoServices } from "../language/iproto-module.js";

export namespace Shape {
	export type LiteralValueType = "boolean" | "int" | "float" | "string";

	export type LiteralValue =
		| { type: "boolean"; value: boolean }
		| { type: "string"; value: string }
		| { type: "int"; value: number }
		| { type: "float"; value: number };

	export type Declaration = Directive | Enum | Exception | Scalar | Endpoint | Struct;

	export type DirectiveLocation =
		| "endpoint"
		| "enum"
		| "exception"
		| "field"
		| "module"
		| "scalar"
		| "struct"
		| "variant";

	export type Type = Enum | Scalar | Struct;

	export interface DirectiveParameter {
		$type: "DirectiveParameter";
		name: string;
		optional: boolean;
		type: LiteralValueType;
		comment: string;
	}

	export interface Directive {
		$type: "Directive";
		locations: Array<DirectiveLocation>;
		name: string;
		parameters: Array<DirectiveParameter>;
		comment: string;
	}

	export interface ScalarOption {
		$type: "ScalarOption";
		name: string;
		type: LiteralValueType;
		comment: string;
	}

	export interface Scalar {
		$type: "Scalar";
		directives: Array<ReferenceDirective>;
		name: string;
		options: Array<ScalarOption>;
		parameters: Array<string>;
		comment: string;
	}

	export interface Enum {
		$type: "Enum";
		directives: Array<ReferenceDirective>;
		members: Array<EnumMember>;
		name: string;
		comment: string;
	}

	export interface EnumMember {
		$type: "EnumMember";
		directives: Array<ReferenceDirective>;
		name: string;
		comment: string;
	}

	export interface Field {
		$type: "Field";
		directives: Array<ReferenceDirective>;
		name: string;
		type: ReferenceType;
		comment: string;
	}

	export interface Struct {
		$type: "Struct";
		directives: Array<ReferenceDirective>;
		mixins: Array<ReferenceType>;
		fields: Array<Field>;
		name: string;
		comment: string;
	}

	export interface Exception {
		$type: "Exception";
		directives: Array<ReferenceDirective>;
		fields: Array<Field>;
		name: string;
		comment: string;
	}

	export interface Endpoint {
		$type: "Endpoint";
		directives: Array<ReferenceDirective>;
		input?: ReferenceType;
		name: string;
		output?: ReferenceType;
		comment: string;
	}

	export interface Module {
		$type: "Module";
		directives: Array<ReferenceDirective>;
		name: string;
		declarations: Array<Declaration>;
		comment: string;
	}

	export interface ReferenceDirectiveArgument {
		$type: "ReferenceDirectiveArgument";
		name: string;
		value: LiteralValue;
	}

	export interface ReferenceDirective {
		$type: "ReferenceDirective";
		$target: string;
		$module: string;
		$name: string;
		arguments: Record<string, LiteralValue>;
	}

	export interface ReferenceType {
		$type: "ReferenceType";
		$target: string;
		$kind: Type["$type"];
		$module: string;
		$name: string;
		arguments: Array<ReferenceType>;
		options: Record<string, LiteralValue>;
	}

	export type Modules = Array<Module>;

	export type ReferenceStringOptions = {
		const?: Extract<LiteralValue, { type: "string" }>;
		max?: Extract<LiteralValue, { type: "int" }>;
		min?: Extract<LiteralValue, { type: "int" }>;
		length?: Extract<LiteralValue, { type: "int" }>;
		nonempty?: Extract<LiteralValue, { type: "boolean" }>;
		pattern?: Extract<LiteralValue, { type: "string" }>;
		email?: Extract<LiteralValue, { type: "boolean" }>;
		uuid?: Extract<LiteralValue, { type: "boolean" }>;
		prefix?: Extract<LiteralValue, { type: "string" }>;
		suffix?: Extract<LiteralValue, { type: "string" }>;
		url?: Extract<LiteralValue, { type: "boolean" }>;
		nanoid?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceFloatOptions = {
		const?: Extract<LiteralValue, { type: "float" }>;
		lt?: Extract<LiteralValue, { type: "float" }>;
		gt?: Extract<LiteralValue, { type: "float" }>;
		lte?: Extract<LiteralValue, { type: "float" }>;
		gte?: Extract<LiteralValue, { type: "float" }>;
		positive?: Extract<LiteralValue, { type: "boolean" }>;
		nonnegative?: Extract<LiteralValue, { type: "boolean" }>;
		negative?: Extract<LiteralValue, { type: "boolean" }>;
		nonpositive?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceIntOptions = {
		const?: Extract<LiteralValue, { type: "int" }>;
		lt?: Extract<LiteralValue, { type: "int" }>;
		gt?: Extract<LiteralValue, { type: "int" }>;
		lte?: Extract<LiteralValue, { type: "int" }>;
		gte?: Extract<LiteralValue, { type: "int" }>;
		positive?: Extract<LiteralValue, { type: "boolean" }>;
		nonnegative?: Extract<LiteralValue, { type: "boolean" }>;
		negative?: Extract<LiteralValue, { type: "boolean" }>;
		nonpositive?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceBooleanOptions = {
		const?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceBinaryOptions = {
		min?: Extract<LiteralValue, { type: "int" }>;
		min_kb?: Extract<LiteralValue, { type: "float" }>;
		min_mb?: Extract<LiteralValue, { type: "float" }>;
		max?: Extract<LiteralValue, { type: "int" }>;
		max_kb?: Extract<LiteralValue, { type: "float" }>;
		max_mb?: Extract<LiteralValue, { type: "float" }>;
		nonempty?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceListOptions = {
		max?: Extract<LiteralValue, { type: "int" }>;
		min?: Extract<LiteralValue, { type: "int" }>;
		length?: Extract<LiteralValue, { type: "int" }>;
		nonempty?: Extract<LiteralValue, { type: "boolean" }>;
	};

	export type ReferenceDictionaryOptions = {
		max?: Extract<LiteralValue, { type: "int" }>;
		min?: Extract<LiteralValue, { type: "int" }>;
		size?: Extract<LiteralValue, { type: "int" }>;
		nonempty?: Extract<LiteralValue, { type: "boolean" }>;
	};
}

export type ShapeOptions = {
	root: string;
};

export class Shape {
	private root: string;
	private services: IprotoServices;

	private ready: Promise<void>;

	constructor(options: ShapeOptions) {
		this.root = path.isAbsolute(options.root) ? options.root : path.resolve(process.cwd(), options.root);
		this.services = new IprotoModuleBuilder(NodeFileSystem).services;

		this.ready = this.init();
	}

	private async init() {
		const workspaceDocumentsService = this.services.shared.workspace.LangiumDocuments;
		const workspaceDocumentBuilder = this.services.shared.workspace.DocumentBuilder;

		const extensions = this.services.LanguageMetaData.fileExtensions;
		const patterns = extensions.map((ext) => `**/*${ext}`);

		const files = await globby(patterns, { cwd: this.root });

		await workspaceDocumentsService.createDocument(URI.parse(BUILTIN_LIBRARY_URI), BUILTIN_LIBRARY);

		for (const file of files.map((file) => path.join(this.root, file))) {
			await workspaceDocumentsService.getOrCreateDocument(URI.file(file));
		}

		await workspaceDocumentBuilder.build(workspaceDocumentsService.all.toArray(), { validation: true });
	}

	private normalizeComment(node: AstNode) {
		const content = this.services.documentation.CommentProvider.getComment(node);
		if (content) {
			return content.slice(2, -2).trim();
		}

		return "";
	}

	private generateModule(module: Module): Shape.Module {
		const declarations: Shape.Declaration[] = [];

		for (const element of module.$container.declaraions) {
			if (element.$type !== "Import" && element.$type !== "Alias") {
				const shape = this.generateDeclaration(element);

				declarations.push(shape);
			}
		}

		const currentDocumentUri = AstUtils.getDocument(module).uri.toString();

		const name = BUILTIN_LIBRARY_URI === currentDocumentUri ? "@" : module.name;

		return {
			$type: module.$type,
			name,
			directives: this.generateReferenceDirectives(module.directives),
			declarations,
			comment: this.normalizeComment(module),
		};
	}

	private generateReferenceType(reference: ReferenceType): Shape.ReferenceType {
		if (!reference.target.ref) {
			throw new Error(`Referenced type '${reference.target.$refText}' is missing its target ref.`);
		}

		const target = reference.target.ref;

		if (target.$type === "Alias") {
			return this.generateReferenceType(target.type);
		}

		const options: Record<string, Shape.LiteralValue> = {};

		for (const option of reference.options) {
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			const optionTarget = option.target.ref!;

			if (optionTarget.type === "string") {
				options[option.target.$refText] = {
					type: "string",
					value: String(option.value?.value),
				};
			}

			if (optionTarget.type === "boolean") {
				options[option.target.$refText] = {
					type: "boolean",
					value: Boolean(option.value?.value ?? true),
				};
			}

			if (optionTarget.type === "int" || optionTarget.type === "float") {
				options[option.target.$refText] = {
					type: optionTarget.type,
					value: Number(option.value?.value ?? true),
				};
			}
		}

		const currentDocumentUri = AstUtils.getDocument(target).uri.toString();

		if (BUILTIN_LIBRARY_URI === currentDocumentUri) {
			if (target.$type === "Scalar" && target.name === "String") {
				const opts = options as Shape.ReferenceStringOptions;

				if (opts.pattern) {
					opts.pattern.value = new RegExp(opts.pattern.value).source;
				}
			}
		}

		let module = "";
		let name = "";

		if (BUILTIN_LIBRARY_URI === currentDocumentUri) {
			module = "@";
			name = target.name;
		} else {
			module = target.$container.module.name;
			name = target.name;
		}

		return {
			$type: reference.$type,
			$target: `${module}::${name}`,
			$kind: target.$type,
			$module: module,
			$name: name,
			options: { ...options },
			arguments: reference.arguments.map((arg) => {
				return this.generateReferenceType(arg);
			}),
		};
	}

	private generateReferenceDirectives(directives: ReferenceDirective[]): Shape.ReferenceDirective[] {
		return directives.map((reference) => {
			if (!reference.target.ref) {
				throw new Error(`Referenced directive '${reference.target.$refText}' is missing its target ref.`);
			}

			const target = reference.target.ref;
			const args: Record<string, Shape.LiteralValue> = {};

			for (const argument of reference.arguments) {
				// biome-ignore lint/style/noNonNullAssertion: <explanation>
				const argumentTarget = argument.target.ref!;

				if (argumentTarget.type === "string") {
					args[argument.target.$refText] = {
						type: "string",
						value: String(argument.value?.value),
					};
				}

				if (argumentTarget.type === "boolean") {
					args[argument.target.$refText] = {
						type: "boolean",
						value: Boolean(argument.value?.value ?? true),
					};
				}

				if (argumentTarget.type === "int" || argumentTarget.type === "float") {
					args[argument.target.$refText] = {
						type: argumentTarget.type,
						value: Number(argument.value?.value ?? true),
					};
				}
			}

			const currentDocumentUri = AstUtils.getDocument(target).uri.toString();

			let module = "";
			let name = "";

			if (BUILTIN_LIBRARY_URI === currentDocumentUri) {
				module = "@";
				name = target.name;
			} else {
				module = target.$container.module.name;
				name = target.name;
			}

			return {
				$type: reference.$type,
				$target: `${module}::${name}`,
				$module: module,
				$name: name,
				arguments: args,
			};
		});
	}

	private generateDeclaration(declaration: Exclude<Declaration, Import | Alias>): Shape.Declaration {
		if (declaration.$type === "Directive") {
			return {
				$type: declaration.$type,
				locations: [...declaration.locations],
				name: declaration.name,
				parameters: declaration.parameters.map((param) => {
					return {
						$type: param.$type,
						name: param.name,
						type: param.type,
						optional: param.optional,
						comment: this.normalizeComment(param),
					};
				}),
				comment: this.normalizeComment(declaration),
			};
		}
		if (declaration.$type === "Enum") {
			return {
				$type: declaration.$type,
				directives: this.generateReferenceDirectives(declaration.directives),
				name: declaration.name,
				members: declaration.members.map((member) => {
					return {
						$type: member.$type,
						directives: this.generateReferenceDirectives(member.directives),
						name: member.name,
						comment: this.normalizeComment(member),
					};
				}),
				comment: this.normalizeComment(declaration),
			};
		}
		if (declaration.$type === "Exception") {
			return {
				$type: declaration.$type,
				name: declaration.name,
				directives: this.generateReferenceDirectives(declaration.directives),
				fields: declaration.fields.map((field) => {
					return {
						$type: field.$type,
						name: field.name,
						directives: this.generateReferenceDirectives(field.directives),
						type: this.generateReferenceType(field.type),
						comment: this.normalizeComment(field),
					};
				}),
				comment: this.normalizeComment(declaration),
			};
		}
		if (declaration.$type === "Scalar") {
			return {
				$type: declaration.$type,
				directives: this.generateReferenceDirectives(declaration.directives),
				name: declaration.name,
				options: declaration.options.map((option) => {
					return {
						$type: option.$type,
						name: option.name,
						type: option.type,
						comment: this.normalizeComment(option),
					};
				}),
				parameters: declaration.parameters.map((param) => param.name),
				comment: this.normalizeComment(declaration),
			};
		}
		if (declaration.$type === "Endpoint") {
			return {
				$type: declaration.$type,
				name: declaration.name,
				directives: this.generateReferenceDirectives(declaration.directives),
				input: declaration.input ? this.generateReferenceType(declaration.input) : undefined,
				output: declaration.output ? this.generateReferenceType(declaration.output) : undefined,
				comment: this.normalizeComment(declaration),
			};
		}
		return {
			$type: declaration.$type,
			name: declaration.name,
			mixins: declaration.mixins.map((ext) => {
				return this.generateReferenceType(ext);
			}),
			directives: this.generateReferenceDirectives(declaration.directives),
			fields: declaration.fields.map((field) => {
				return {
					$type: field.$type,
					name: field.name,
					type: this.generateReferenceType(field.type),
					directives: this.generateReferenceDirectives(field.directives),
					comment: this.normalizeComment(field),
				};
			}),
			comment: this.normalizeComment(declaration),
		};
	}

	async generate() {
		await this.ready;

		const documents = this.services.shared.workspace.LangiumDocuments;

		const modules: Shape.Modules = [];

		for (const document of documents.all) {
			const diagnostics = document.diagnostics ?? [];
			const file = document.parseResult.value as File;
			const errors = diagnostics.filter((e) => e.severity === DiagnosticSeverity.Error);

			if (errors.length > 0) {
				let message = "There are validation errors: \n";

				for (const error of errors) {
					message += ` - ${document.uri.fsPath} [${error.range.start.line + 1}]: ${error.message}\n`;
				}

				throw new Error(message);
			}

			modules.push(this.generateModule(file.module));
		}

		return modules;
	}
}
